import { Board } from "../board";
import { BOARD_COLS, BOARD_ROWS } from "../constants";
import type { PieceType, Side, Square } from "../types";

const TYPE_CHAR: Record<PieceType, string> = {
  pawn: "p",
  knight: "n",
  bishop: "b",
  rook: "r",
  queen: "q",
  king: "k",
};

/**
 * Convert internal Board to a FEN string.
 *
 * Row/file mapping:
 *   row 0 (top of grid, enemy side) -> rank 8 (first FEN field)
 *   row 7 (bottom, player side)     -> rank 1
 *   col 0..7                        -> file a..h
 *
 * Side mapping: player -> white ('w'), enemy -> black ('b').
 * Castling and en-passant are omitted because the rogue mode does not track them.
 */
export function boardToFen(board: Board, sideToMove: Side): string {
  const ranks: string[] = [];
  for (let r = 0; r < BOARD_ROWS; r++) {
    let row = "";
    let empty = 0;
    for (let c = 0; c < BOARD_COLS; c++) {
      const piece = board.get({ row: r, col: c });
      if (!piece) {
        empty++;
        continue;
      }
      if (empty > 0) {
        row += String(empty);
        empty = 0;
      }
      const ch = TYPE_CHAR[piece.type];
      row += piece.side === "player" ? ch.toUpperCase() : ch;
    }
    if (empty > 0) row += String(empty);
    ranks.push(row);
  }
  const stm = sideToMove === "player" ? "w" : "b";
  return `${ranks.join("/")} ${stm} - - 0 1`;
}

export interface UciMove {
  from: Square;
  to: Square;
  promotion?: string;
}

/** Parse a UCI move like "e7e5" or "e7e8q" into board squares. */
export function uciToSquares(uci: string): UciMove | null {
  if (!uci || uci.length < 4) return null;
  const fileFrom = uci.charCodeAt(0) - 97; // 'a'
  const rankFrom = parseInt(uci[1], 10);
  const fileTo = uci.charCodeAt(2) - 97;
  const rankTo = parseInt(uci[3], 10);
  if (Number.isNaN(rankFrom) || Number.isNaN(rankTo)) return null;
  if (fileFrom < 0 || fileFrom > 7 || fileTo < 0 || fileTo > 7) return null;
  if (rankFrom < 1 || rankFrom > 8 || rankTo < 1 || rankTo > 8) return null;
  return {
    from: { row: BOARD_ROWS - rankFrom, col: fileFrom },
    to: { row: BOARD_ROWS - rankTo, col: fileTo },
    promotion: uci[4],
  };
}
